export type FaturaStatus =
  | 'EMITIDA'
  | 'PAGA'
  | 'VENCIDA'
  | 'CANCELADA'
  | 'ESTORNADA'
  | 'ERRO_EMISSAO';

export type FaturaTipoGeracao = 'MANUAL' | 'EM_LOTE' | 'AGENDADA';

export type FaturaTipoCliente = 'CONVENIADO' | 'AVULSO';

export type FaturaOrigemPagamento = 'ASAAS' | 'MANUAL';

export type MetodoPagamento = 'PIX' | 'BOLETO' | 'CARTAO';

export type FaturaHistoricoAcao =
  | 'CRIADA'
  | 'EMITIDA_ASAAS'
  | 'PAGAMENTO_CONFIRMADO'
  | 'PAGAMENTO_MANUAL'
  | 'SEGUNDA_VIA_ENVIADA'
  | 'VENCIDA_AUTOMATICA'
  | 'CANCELADA'
  | 'CANCELADA_POR_REABERTURA'
  | 'REEMITIDA'
  | 'ESTORNADA'
  | 'ERRO_EMISSAO_ASAAS'
  | 'COMPROVANTE_ANEXADO';

export type CanalEnvio = 'EMAIL' | 'WHATSAPP';

export interface FaturaAsaasInfo {
  asaasId: string | null;
  linkPagamento: string | null;
  metodoPagamento: MetodoPagamento | null;
  statusAsaas: string | null;
  erroEmissao: string | null;
  atualizadoEm: string | null;
}

export interface FaturaBreakdownItem {
  categoria: string;
  categoriaLabel: string;
  quantidade: number;
  valorUnitario: number | null;
  subtotal: number;
  desconto: number;
}

export type ConsumoOrigemTipo =
  | 'NOTIFICACAO_EXTRAJUDICIAL'
  | 'LOTE_NOTIFICACOES'
  | 'AR_CORREIOS'
  | 'SEDEX'
  | 'REENVIO_POSTAL'
  | 'NEGOCIACAO'
  | 'PROCESSO_ARBITRAL'
  | 'PETICIONAMENTO'
  | 'OUTRO';

export interface ConsumoOrigemRef {
  tipo: ConsumoOrigemTipo;
  identificador: string;
  rotulo: string;
}

export interface ConsumoLinhaDetalhe {
  id: string;
  data: string;
  categoria: string;
  servicoLabel: string;
  origemPrincipal: ConsumoOrigemRef;
  vinculacoes: ConsumoOrigemRef[];
  destinatarioNome: string | null;
  destinatarioDocumento: string | null;
  valorUnitario: number;
  desconto: number;
  valorCobrado: number;
  observacao: string | null;
}

export interface FaturaComprovanteManual {
  arquivoNome: string;
  arquivoTipo: 'IMAGEM' | 'PDF';
  arquivoUrl: string;
  dataPagamento: string;
  valorConfirmado: number;
  observacao: string | null;
  anexadoPor: string;
  anexadoEm: string;
}

export interface FaturaHistoricoEntry {
  id: string;
  acao: FaturaHistoricoAcao;
  usuarioId: string | null;
  usuarioNome: string | null;
  timestamp: string;
  descricao: string;
  detalhes: Record<string, unknown> | null;
}

export interface FaturaSegundaViaEnvio {
  id: string;
  canais: CanalEnvio[];
  destinatarioEmail: string | null;
  destinatarioWhatsapp: string | null;
  mensagemPersonalizada: string | null;
  enviadoPor: string;
  enviadoEm: string;
}

export interface AdminContact {
  email: string | null;
  telefone: string | null;
  nome: string | null;
}

export interface Fatura {
  id: string;
  numero: string;
  tenantId: string;
  tenantNome: string;
  tipoCliente: FaturaTipoCliente;
  contratoId: string | null;
  produtoNome: string | null;
  apuracaoId: string | null;
  apuracaoPeriodoInicio: string | null;
  apuracaoPeriodoFim: string | null;
  loteId: string | null;
  faturaOriginalId: string | null;
  faturaReemitidaId: string | null;
  status: FaturaStatus;
  tipoGeracao: FaturaTipoGeracao;
  origemPagamento: FaturaOrigemPagamento | null;
  valorTotal: number;
  valorPago: number | null;
  descricao: string;
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento: string | null;
  dataCancelamento: string | null;
  motivoCancelamento: string | null;
  asaas: FaturaAsaasInfo;
  breakdown: FaturaBreakdownItem[];
  extratoConsumo: ConsumoLinhaDetalhe[];
  comprovanteManual: FaturaComprovanteManual | null;
  comprovanteUrl?: string | null;
  adminContact?: AdminContact | null;
  historico: FaturaHistoricoEntry[];
  enviosSegundaVia: FaturaSegundaViaEnvio[];
  geradoPor: string | null;
  ehFaturaCorreioSeparada: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

export interface FaturaListFilters {
  busca: string;
  status: FaturaStatus[];
  tipoGeracao: FaturaTipoGeracao[];
  tipoCliente: FaturaTipoCliente[];
  vencimentoInicio: string | null;
  vencimentoFim: string | null;
  emissaoInicio: string | null;
  emissaoFim: string | null;
  somenteComErroEmissao: boolean;
}

export interface FaturaListSummary {
  totalEmitido: number;
  totalPago: number;
  totalAReceber: number;
  totalVencido: number;
  quantidadeComErroEmissao: number;
}

export interface GerarFaturaManualFormData {
  tenantId: string;
  valor: number;
  descricao: string;
  dataVencimento: string;
  metodoPagamento: MetodoPagamento;
  customerDocument: string;
}

export interface GerarFaturasLoteFormData {
  apuracoesIds: string[];
}

export interface GerarFaturaDeApuracaoFormData {
  apuracaoId: string;
  tenantId: string;
  tenantNome: string;
  contratoId: string;
  produtoNome: string;
  tipoCliente: FaturaTipoCliente;
  periodoInicio: string;
  periodoFim: string;
  diaVencimento: number;
  valorTotal: number;
  breakdown: FaturaBreakdownItem[];
}

export interface CancelarFaturaFormData {
  faturaId: string;
  motivo: string;
  cienteEnvioCancelado: boolean;
}

export interface ReemitirFaturaFormData {
  faturaId: string;
  valor: number;
  dataVencimento: string;
  descricao: string;
  motivo: string;
}

export interface EnviarSegundaViaFormData {
  faturaId: string;
  canais: CanalEnvio[];
  mensagemPersonalizada: string | null;
}

export interface AnexarComprovanteFormData {
  faturaId: string;
  arquivoNome: string;
  arquivoTipo: 'IMAGEM' | 'PDF';
  dataPagamento: string;
  valorConfirmado: number;
  observacao: string | null;
  file: File;
}

export interface FaturasState {
  faturas: Fatura[];
  summary: FaturaListSummary;
  filters: FaturaListFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

export const DEFAULT_FATURA_FILTERS: FaturaListFilters = {
  busca: '',
  status: [],
  tipoGeracao: [],
  tipoCliente: [],
  vencimentoInicio: null,
  vencimentoFim: null,
  emissaoInicio: null,
  emissaoFim: null,
  somenteComErroEmissao: false,
};

export const DEFAULT_FATURA_SUMMARY: FaturaListSummary = {
  totalEmitido: 0,
  totalPago: 0,
  totalAReceber: 0,
  totalVencido: 0,
  quantidadeComErroEmissao: 0,
};
